/**
 * Read a newline-delimited JSON (NDJSON) streaming fetch response, parsing each
 * line and invoking `onLine` per object. Malformed lines are skipped (not
 * thrown) so a single bad line from the server can't abort the whole stream —
 * the caller can decide via `onError` whether to surface it.
 *
 * Returns the accumulated array of parsed objects when the stream completes.
 */
export async function readNdjsonStream<T>(
  response: Response,
  onLine: (value: T) => void,
  onError?: (line: string, err: unknown) => void,
): Promise<T[]> {
  if (!response.ok || !response.body) {
    throw new Error(`Stream failed: ${response.status} ${response.statusText}`);
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  const results: T[] = [];
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buffer.indexOf('\n')) >= 0) {
      const line = buffer.slice(0, nl).trim();
      buffer = buffer.slice(nl + 1);
      if (!line) continue;
      try {
        const parsed = JSON.parse(line) as T;
        results.push(parsed);
        onLine(parsed);
      } catch (err) {
        onError?.(line, err);
      }
    }
  }
  return results;
}
